/**
 * 營收分潤 API 服務層
 *
 * 呼叫 Cloud Functions HTTP endpoints（onRequest）
 *
 * 2026-04 架構簡化：
 *   - 每劇 paid_coin_rate 改走 drama 文件 API（見 @/services/firebase/drama.ts updateDrama）
 *   - 移除 getSystemRevenueConfig 以外的 config CRUD endpoints
 */
import { getAuth } from "firebase/auth";
import { app } from "./init";
import type {
  SystemRevenueConfig,
  RevenueOverview,
  PlatformRevenueOverview,
  IapPlatform,
} from "@/types/revenue";

// ============================================================
// 基礎工具
// ============================================================

function getBaseUrl(): string {
  const projectId =
    app.options.projectId ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    "onreel-dev";
  return `https://asia-east1-${projectId}.cloudfunctions.net`;
}

async function getIdToken(): Promise<string> {
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error("用戶未登入");
  return user.getIdToken();
}

async function apiGet<T>(functionName: string, params?: Record<string, string>): Promise<T> {
  const token = await getIdToken();
  const url = new URL(`${getBaseUrl()}/${functionName}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v) url.searchParams.set(k, v);
    });
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

async function apiPost<T>(functionName: string, body: Record<string, any>): Promise<T> {
  const token = await getIdToken();
  const res = await fetch(`${getBaseUrl()}/${functionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ============================================================
// System config 讀取（主要用於取得 monthly_free_coin_rates 歷史）
// ============================================================

export async function getSystemRevenueConfig(): Promise<SystemRevenueConfig> {
  const data = await apiGet<{ config: SystemRevenueConfig }>("getSystemRevenueConfig");
  return data.config || {};
}

// ============================================================
// 月度 free coin 費率公告 API
// ============================================================

export async function getMonthlyFreeCoinRate(
  period: string
): Promise<{ period: string; rate: number }> {
  return apiGet("getMonthlyFreeCoinRate", { period });
}

export async function setMonthlyFreeCoinRate(
  period: string,
  rate: number,
  note?: string
): Promise<void> {
  await apiPost("setMonthlyFreeCoinRate", { period, rate, ...(note ? { note } : {}) });
}

// ============================================================
// Apple/Google 平台抽成費率
// ============================================================

export async function setPlatformFeeRate(
  platform: IapPlatform,
  period: string,
  rate: number,
  note?: string
): Promise<void> {
  await apiPost("setPlatformFeeRate", {
    platform,
    period,
    rate,
    ...(note ? { note } : {}),
  });
}

// ============================================================
// 營收查詢 API
// ============================================================

export async function getRevenueOverview(period?: string): Promise<RevenueOverview> {
  return apiGet("getRevenueOverview", period ? { period } : undefined);
}

// ============================================================
// Platform Revenue Overview（新儀表板）
// ============================================================

export async function getPlatformRevenueOverview(opts: {
  period?: string;
  compare?: boolean;
}): Promise<{ success: true } & PlatformRevenueOverview> {
  const params: Record<string, string> = {};
  if (opts.period) params.period = opts.period;
  if (opts.compare) params.compare = "true";
  return apiGet("getPlatformRevenueOverview", params);
}

export type RevenueRangeOpts =
  | { startDate: string; endDate: string }
  | { period: string }
  | undefined;

function rangeToParams(opts: RevenueRangeOpts): Record<string, string> {
  if (!opts) return {};
  if ("startDate" in opts) {
    return { start_date: opts.startDate, end_date: opts.endDate };
  }
  return { period: opts.period };
}

export async function getCompanyRevenue(
  companyId: string,
  opts?: RevenueRangeOpts
): Promise<any> {
  return apiGet("getCompanyRevenue", {
    company_id: companyId,
    ...rangeToParams(opts),
  });
}

export async function getDramaRevenue(
  dramaId: string,
  opts?: RevenueRangeOpts
): Promise<any> {
  return apiGet("getDramaRevenue", {
    drama_id: dramaId,
    ...rangeToParams(opts),
  });
}

export interface DramaPerformanceItem {
  drama_id: string;
  drama_title: string;
  company_id: string;
  company_name: string;
  drama_play_times_lifetime: number;
  watcher_count: number;
  unlock_count: number;
  full_drama_count: number;
  paid_coins: number;
  free_coins: number;
  total_coins: number;
  unique_unlock_users: number;
  paid_coin_rate_applied: number;
  paid_rate_set: boolean;
  free_coin_rate_applied: number;
  calculated_amount: number;
  platform_revenue_ntd: number;
}

/**
 * 取得指定期間每部劇的表現統計
 */
export async function getDramaPerformance(opts: {
  startDate: string;
  endDate: string;
}): Promise<{
  start_date: string;
  end_date: string;
  total_dramas: number;
  period_iap_gross_ntd: number;
  period_iap_net_ntd: number;
  period_paid_coin_sold: number;
  period_avg_paid_coin_net_value: number;
  dramas: DramaPerformanceItem[];
}> {
  return apiGet("getDramaPerformance", {
    start_date: opts.startDate,
    end_date: opts.endDate,
  });
}

// ============================================================
// CSV 匯出 API
// ============================================================

export type CsvGranularity =
  | "detail"
  | "daily_company_drama"
  | "daily_company"
  | "company_drama"
  | "company";

/**
 * 觸發瀏覽器下載指定期間的分潤試算 CSV。
 */
export async function exportTransactionsCSV(opts: {
  startDate: string;
  endDate: string;
  granularity?: CsvGranularity;
  companyIds?: string[];
  dramaIds?: string[];
}): Promise<void> {
  const token = await getIdToken();
  const url = new URL(`${getBaseUrl()}/exportTransactionsCSV`);
  url.searchParams.set("start_date", opts.startDate);
  url.searchParams.set("end_date", opts.endDate);
  url.searchParams.set("granularity", opts.granularity || "detail");
  if (opts.companyIds?.length) url.searchParams.set("company_ids", opts.companyIds.join(","));
  if (opts.dramaIds?.length) url.searchParams.set("drama_ids", opts.dramaIds.join(","));

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();

  let filename = `onreel_revenue_${opts.startDate}_${opts.endDate}.csv`;
  const cd = res.headers.get("content-disposition");
  if (cd) {
    const m = cd.match(/filename="?([^";]+)"?/i);
    if (m) filename = m[1];
  }

  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(downloadUrl);
}
